/**
 * Shared utility functions for Product Details Modals
 * This file contains reusable logic to reduce code duplication between
 * BulkProductDetailsModal and ProductDetailsModal while maintaining exact same logic
 */

import {Constants} from '@/utils/constants/commonConstants';
import {
  computeLeftoverForQuota,
  getQuotaDecreaseError
} from '@/utils/rewardPayloadUtils';

// Initial form data structure
export const getInitialFormData = () => ({
  points: '',
  quota: {
    unlimited: false,
    value: ''
  },
  inventory: ''
});

// Initial errors structure
export const getInitialErrors = () => ({
  points: '',
  quota: '',
  inventory: ''
});

// Initialize form data from product details (editable fields only)
export const initializeFormDataFromProduct = (product) => {
  if (!product?.details) return getInitialFormData();

  const initialInventory = product.details.inventory || '';
  return {
    points: product.details.points || '',
    quota: {
      unlimited: product.details.quota?.unlimited || false,
      value: product.details.quota?.value || ''
    },
    inventory: initialInventory
  };
};

/**
 * Resolve inventory for a quota value change.
 * ACTIVE with numeric originals → leftover delta semantics (Attributes parity).
 * Otherwise → draft 1:1 sync (inventory = quota value).
 */
export const resolveInventoryForQuotaChange = ({
  value,
  isActive,
  originalQuota,
  originalInventory
}) => {
  const hasNumericOriginals =
    Number.isFinite(Number(originalQuota)) &&
    Number.isFinite(Number(originalInventory)) &&
    originalQuota !== '' &&
    originalInventory !== '' &&
    originalInventory !== Constants.Unlimited;

  if (!isActive || !hasNumericOriginals) {
    return value;
  }

  return computeLeftoverForQuota({
    value,
    isInProgress: true,
    originalQuota,
    originalLeftOver: originalInventory
  });
};

/**
 * Merge applied modal details onto a product while preserving/seeding originals.
 * Never overwrite originals after first set; never seed from Unlimited.
 */
export const mergeProductDetailsWithOriginals = (
  existingDetails = {},
  updatedDetails = {}
) => {
  const nextDetails = {
    ...existingDetails,
    ...updatedDetails
  };

  const existingOriginalQuota = existingDetails?.originalQuota;
  const existingOriginalInventory = existingDetails?.originalInventory;

  const hasExistingOriginalQuota =
    existingOriginalQuota !== undefined &&
    existingOriginalQuota !== null &&
    existingOriginalQuota !== '';
  const hasExistingOriginalInventory =
    existingOriginalInventory !== undefined &&
    existingOriginalInventory !== null &&
    existingOriginalInventory !== '';

  if (hasExistingOriginalQuota) {
    nextDetails.originalQuota = existingOriginalQuota;
  } else if (
    !updatedDetails?.quota?.unlimited &&
    updatedDetails?.quota?.value &&
    updatedDetails.quota.value !== ''
  ) {
    nextDetails.originalQuota = String(updatedDetails.quota.value);
  }

  if (hasExistingOriginalInventory) {
    nextDetails.originalInventory = existingOriginalInventory;
  } else if (
    updatedDetails?.inventory &&
    updatedDetails.inventory !== '' &&
    updatedDetails.inventory !== Constants.Unlimited
  ) {
    nextDetails.originalInventory = String(updatedDetails.inventory);
  }

  return nextDetails;
};

// Create points change handler
export const createPointsHandler =
  (validatePoints, setErrors, setFormData) => (value) => {
    const validationError = validatePoints(value);
    setErrors((prev) => ({
      ...prev,
      points: validationError || ''
    }));
    setFormData((prev) => ({
      ...prev,
      points: value
    }));
  };

// Create inventory change handler
export const createInventoryHandler =
  (validateInventory, setErrors, setFormData) => (value) => {
    const validationError = validateInventory(value);
    setErrors((prev) => ({
      ...prev,
      inventory: validationError || ''
    }));
    setFormData((prev) => ({
      ...prev,
      inventory: value
    }));
  };

/**
 * Create quota change handler.
 * @param {Function} validateQuotaValue - Modal quota format validator
 * @param {Function} setErrors
 * @param {Function} setFormData
 * @param {Object} formData - Current form data (for unlimited flag)
 * @param {Object} options
 * @param {boolean} options.isActive - Reward status === ACTIVE
 * @param {string} options.originalQuota - Baseline quota from load/seed
 * @param {string} options.originalInventory - Baseline inventory from load/seed
 */
export const createQuotaHandler =
  (
    validateQuotaValue,
    setErrors,
    setFormData,
    formData,
    {isActive = false, originalQuota = '', originalInventory = ''} = {}
  ) =>
  (field, value) => {
    if (field === 'unlimited') {
      // When toggling unlimited, clear quota error (including decrease error)
      setErrors((prev) => ({
        ...prev,
        quota: ''
      }));
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          quota: {
            ...prev.quota,
            unlimited: value
          }
        };

        // If unlimited is true, set inventory to "Unlimited"
        if (value) {
          newFormData.inventory = Constants.Unlimited;
        } else {
          // If unlimited is false, set inventory to quota value or empty
          newFormData.inventory = prev.quota.value || '';
        }

        return newFormData;
      });
    } else if (field === 'value') {
      // Validate quota value only if unlimited is false
      const baseError = formData.quota.unlimited
        ? ''
        : validateQuotaValue(value);
      const decreaseError = formData.quota.unlimited
        ? null
        : getQuotaDecreaseError(value, originalQuota, isActive);
      const validationError = baseError || decreaseError || '';

      setErrors((prev) => ({
        ...prev,
        quota: validationError
      }));
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          quota: {
            ...prev.quota,
            value: value
          }
        };

        if (!prev.quota.unlimited) {
          newFormData.inventory = resolveInventoryForQuotaChange({
            value,
            isActive,
            originalQuota,
            originalInventory
          });
        }

        return newFormData;
      });
    }
  };

// Create form validator
export const createFormValidator =
  (
    validatePoints,
    validateQuotaValue,
    formData,
    {isActive = false, originalQuota = ''} = {}
  ) =>
  () => {
    // Points must be valid
    const pointsError = validatePoints(formData.points);
    if (pointsError) return false;

    // Quota value must be valid only if unlimited is false
    if (!formData.quota.unlimited) {
      const quotaError = validateQuotaValue(formData.quota.value);
      if (quotaError) return false;

      const decreaseError = getQuotaDecreaseError(
        formData.quota.value,
        originalQuota,
        isActive
      );
      if (decreaseError) return false;
    }

    // Inventory is not required as it's always auto-filled
    return true;
  };

// Create form validator with error setting
export const createFormValidatorWithErrors =
  (
    validatePoints,
    validateQuotaValue,
    formData,
    setErrors,
    {isActive = false, originalQuota = ''} = {}
  ) =>
  () => {
    const newErrors = {};

    // Validate points
    const pointsError = validatePoints(formData.points);
    if (pointsError) {
      newErrors.points = pointsError;
    }

    // Validate quota value only if unlimited is false
    if (!formData.quota.unlimited) {
      const quotaError = validateQuotaValue(formData.quota.value);
      const decreaseError = getQuotaDecreaseError(
        formData.quota.value,
        originalQuota,
        isActive
      );
      if (quotaError) {
        newErrors.quota = quotaError;
      } else if (decreaseError) {
        newErrors.quota = decreaseError;
      }
    }

    // Inventory is not required as it's always auto-filled
    // No inventory validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

// Check if inventory field should be disabled (always disabled now)
export const isInventoryDisabled = () => true;
